import {
  Box,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import NewTableRow from '../../../../components/TableElements/NewTableRow'
import NotFoundTableRow from '../../../../components/TableElements/NotFoundTableRow'
import HeadPagination from '../../../../components/TableElements/Pagination/HeadPagination'
import NewPagination from '../../../../components/TableElements/Pagination/NewPagination'
import TableHeaderCell from '../../../../components/TableElements/TableHeaderCell'
import TableLoaderElement from '../../../../components/TableElements/TableLoaderElement'
import { sortedKeys, widthMap } from './consts/table.consts'
import { getCellContent } from './helpers/getCellContent'
import { requestsPaginationStore } from './hooks/useRequestsData'
import { useRequestsTable } from './hooks/useRequestTable'
import TableScrollButtons from './NewTableScrollButtons'
import { RequestClarificationType } from './types/types'

function RequestsTable({
  openRequestDetails,
  filterStore,
}: {
  openRequestDetails: any
  filterStore: any
}) {
  const {
    setRenderTable,
    isLoading,
    data,
    mapHeadTitles,
    sort,
    onSort,
    selectedRow,
    columns,
    onRowClick,
    categories,
  } = useRequestsTable(openRequestDetails, filterStore)

  return (
    <Box
      sx={{
        background: '#FFF',
        borderRadius: '8px',
        boxShadow: ' 0px 0px 4px 0px rgba(0, 0, 0, 0.15)',
        padding: ' 0 8px 24px',
        overflow: 'hidden',
        overflowX: 'hidden',
      }}
    >
      <HeadPagination
        contentLength={data?.content?.length ?? 0}
        totalSize={data?.totalSize ?? 0}
        rowsPerPageOptions={[50, 100, 200, 300, 400, 500]}
        store={requestsPaginationStore}
        setRenderTable={setRenderTable}
      />
      <Box
        sx={{ overflow: isLoading ? 'hidden' : 'auto' }}
        id={'custom-table-container'}
      >
        <Table
          sx={{
            width: '100%',
            boxShadow: 'none',
            tableLayout: 'fixed',
            minWidth: '1300px',
          }}
          aria-label='simple table'
        >
          <TableHead>
            <TableRow>
              {columns.map((key) => {
                const sortKey =
                  key === 'financialSourceId' ? 'financial-source' : key

                return (
                  <TableHeaderCell
                    align={'left'}
                    key={key}
                    propKey={key}
                    sort={sort}
                    sortKey={sortKey}
                    onSort={
                      sortedKeys.includes(key)
                        ? () => onSort(sortKey)
                        : undefined
                    }
                    width={key in widthMap ? widthMap[key] : undefined}
                  >
                    <Typography
                      variant={'body1'}
                      sx={{
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: navigator.userAgent.includes('Firefox')
                          ? '400'
                          : '500',
                      }}
                    >
                      {mapHeadTitles[key as keyof RequestClarificationType]}
                    </Typography>
                  </TableHeaderCell>
                )
              })}
            </TableRow>
          </TableHead>
          {isLoading && <TableLoaderElement colSpan={8} width={'90vw'} />}
          {!isLoading && (
            <TableBody>
              {!!data?.content?.length &&
                data.content.map((el, idx) => (
                  <NewTableRow
                    key={el?.id}
                    onClick={() => onRowClick(el)}
                    outline={selectedRow?.id === el.id}
                  >
                    {Object.keys(mapHeadTitles).map((key) =>
                      getCellContent({
                        key,
                        element: el,
                        index: idx,
                        categories,
                      })
                    )}
                  </NewTableRow>
                ))}
              {!data?.content.length && <NotFoundTableRow columnsLength={8} />}
            </TableBody>
          )}
        </Table>
      </Box>
      {!isLoading && !!data?.content?.length && (
        <TableScrollButtons rowsLength={data?.content.length} />
      )}
      <Stack
        direction={'row'}
        justifyContent={'end'}
        padding={'36px 16px 16px 16px'}
      >
        <NewPagination
          hidePageSign={(data?.totalSize ?? 0) < 50}
          totalSize={data?.totalSize ?? 0}
          rowsPerPageOptions={[50, 100, 200, 300, 400, 500]}
          store={requestsPaginationStore}
          setRenderTable={setRenderTable}
        />
      </Stack>
    </Box>
  )
}

export default observer(RequestsTable)
